import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plane, Lock, CheckCircle2, Circle, Loader2, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";

const DECISION_TONE = {
  drone_approved: "bg-emerald-50 text-emerald-600 border-emerald-200",
  drone_not_recommended: "bg-amber-50 text-amber-700 border-amber-200",
  drone_unnecessary: "bg-slate-50 text-slate-600 border-slate-200",
  unsafe_for_deployment: "bg-red-50 text-red-600 border-red-200",
  insufficient_information: "bg-amber-50 text-amber-700 border-amber-200",
  escalated: "bg-red-50 text-red-600 border-red-200",
};

export default function DroneRequestCard({ caseId }) {
  const [gate, setGate] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/cases/${caseId}/drone-eligibility`).then(({ data }) => setGate(data)).catch(() => setGate(null));
  }, [caseId]);
  useEffect(load, [load]);

  if (!gate || gate.legacy) return null;

  const request = async () => {
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/drone-request`, { confirm: true });
      toast.success("Drone deployment requested — eligible pilots in your area are being notified.");
      setConfirmOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not request drone deployment");
    } finally { setBusy(false); }
  };

  const d = gate.dispatch;
  const decisionLabel = gate.rn_drone_decision_label;

  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="drone-request-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Plane className="w-5 h-5 text-cobalt" />
          <h3 className="font-heading font-semibold text-navy">Drone Scene Documentation</h3>
        </div>
        {decisionLabel && (
          <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${DECISION_TONE[gate.rn_drone_decision] || "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid="rn-drone-decision-badge">
            RN: {decisionLabel}
          </span>
        )}
      </div>

      {d ? (
        <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-sm" data-testid="drone-dispatch-status">
          <div className="font-semibold text-navy capitalize">Deployment status: {d.status.replace(/_/g, " ")}</div>
          <div className="mt-1 text-xs text-slate-500 space-y-0.5">
            {d.requested_at && <div>Requested {new Date(d.requested_at).toLocaleString()}</div>}
            {d.accepted_at && <div>Pilot accepted {new Date(d.accepted_at).toLocaleString()}</div>}
            {d.arrived_at && <div>On scene {new Date(d.arrived_at).toLocaleString()}</div>}
            {d.completed_at && <div>Completed {new Date(d.completed_at).toLocaleString()} — footage saved to your Evidence Locker</div>}
            {d.cancelled_at && <div>Cancelled {new Date(d.cancelled_at).toLocaleString()}</div>}
          </div>
        </div>
      ) : gate.unlocked ? (
        <div className="mt-4">
          <p className="text-sm text-slate-600">{gate.service_description}</p>
          <p className="mt-2 text-xs text-slate-400">{gate.cost_note}</p>
          {gate.weather_advisory?.summary && <p className="mt-2 text-xs text-slate-500">Weather advisory: {gate.weather_advisory.summary}</p>}
          {!confirmOpen ? (
            <button onClick={() => setConfirmOpen(true)} data-testid="drone-request-btn"
              className="mt-4 flex items-center gap-2 px-6 py-3 rounded-full bg-cobalt text-white font-medium hover:bg-cobalt-hover transition-all shadow-md">
              <Plane className="w-4 h-4" /> Request Drone Deployment
            </button>
          ) : (
            <div className="mt-4 rounded-2xl border border-slate-200 p-4">
              <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="drone-confirm-checkbox" className="mt-0.5 accent-cobalt" />
                I confirm that I am requesting Crash Ready drone scene documentation for this accident and understand the service and cost information above.
              </label>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setConfirmOpen(false)} className="px-4 py-2 rounded-full bg-white border border-slate-200 text-sm text-slate-600">Cancel</button>
                <button onClick={request} disabled={!confirmed || busy} data-testid="drone-confirm-btn"
                  className="px-5 py-2 rounded-full bg-navy text-white text-sm font-semibold disabled:opacity-40 flex items-center gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plane className="w-4 h-4" />} Confirm Request
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="mt-4 rounded-2xl bg-slate-50 border border-slate-200 p-4" data-testid="drone-locked">
          <div className="flex items-center gap-2 text-slate-600 text-sm font-medium">
            <Lock className="w-4 h-4" /> {gate.locked_message}
          </div>
        </div>
      )}

      <button onClick={() => setShowChecklist((v) => !v)} data-testid="drone-checklist-toggle"
        className="mt-4 flex items-center gap-1.5 text-xs font-medium text-cobalt hover:underline">
        <ShieldCheck className="w-3.5 h-3.5" /> Deployment requirements {showChecklist ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {showChecklist && (
        <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5" data-testid="drone-checklist">
          {gate.stages.map((s) => (
            <div key={s.key} className="flex items-center gap-2 text-xs" data-testid={`drone-stage-${s.key}`}>
              {s.done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> : <Circle className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
              <span className={s.done ? "text-slate-600" : "text-slate-400"}>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
